// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import moment from 'moment-timezone';
import BackButton from '../../Elements/BackButton';
import BarChart from '../../BarChart/BarChart';
import AnimatedUsage from '../../Elements/AnimatedUsage';
import { apiUrl } from '../../../config/constants';
import TimeframeSelector from '../../Elements/TimeframeSelector';
import NavigationArrows from '../../Elements/NavigationArrows';
import fillDates from '../../../utils/fillDates';
import LoginStore from '../../../store/LoginStore';
import { UsageData, Customer, Contract, DataPoint } from '../../../store/index';
import { Tick } from '../../BarChart/index';
import Loading from './Loading';
import getUsageData from '../../../utils/getUsageData';
import ExcelButton from '../../Elements/ExcelButton';
import { ReactComponent as Chevron } from '../../../assets/images/chevron-up.svg';
import TimeStepSelector from '../../Elements/TimeStepSelector';
// Set Finnish locale, required for month names
import 'moment/locale/fi';
import buildQueryString from '../../../utils/buildQueryString';
import TotalUsageCircle from '../Company/TotalUsageCircle';
import { getCompanyUserDetails } from '../../../actions/b2bUsers';
import { Customerships } from '../../../types/companies';
import GraphTitle from '../../BarChart/GraphTitle';
moment.locale('fi');

type RouterProps = {
  navigate: ReturnType<typeof useNavigate>;
  location: ReturnType<typeof useLocation>;
  params: ReturnType<typeof useParams> & { initialDate?: string; itemId?: string; companyId?: string };
};

type Props = {
  LoginStore: typeof LoginStore;
  customer?: Customer;
  contract: Contract;
  id: string;
  type: string;
} & RouterProps;

type Params = {
  [key: string]: string;
  start: string;
  end: string;
  level: string;
  frame: string;
};

type State = {
  axisClass: string;
  barsHidden: boolean;
  errorMessage: string;
  loading: boolean;
  sum: number;
  productionSum?: number | undefined;
  tick?: Tick | null;
  timeframe: string;
  usageData?: UsageData | null | undefined;
  showComparison: boolean;
  showTemperature: boolean;
  timeframeExceeded: boolean;
  goToDashboard: boolean;
  isCompany: boolean;
  companies: Customerships[] | null;
  showInfo: boolean;
  frontPageLink: string;
  maximumResolution: string;
  unit: string;
  currentPeriodTemperatureFetched: boolean;
  currentPeriodElectricityFetched: boolean;
  consumptionDataFailed: boolean;
  chartLoading: boolean;
};

const subtractOne = (ts: string, frame: moment.DurationInputArg2) =>
  moment(ts)
    .subtract(1, frame)
    .format('YYYY-MM-DD');

const mergeForComparison = (data: DataPoint[], data2: DataPoint[] | undefined | false | null) => {
  let i = 0;
  while (data && i < data.length) {
    if (!data2 || i === data2.length) break;
    data[i].comparison = {
      v: data2[i].v,
      r: data2[i].r,
      ts: data2[i].ts,
      vp: data2[i]?.vp
    };
    ++i;
  }
  return data;
};

class DetailsPage extends React.Component<Props, State> {
  //eslint-disable-next-line
  backListener: any;
  constructor(props: Props) {
    super(props);

    this.state = {
      sum: 0,
      timeframe: '',
      barsHidden: true,
      axisClass: '',
      errorMessage: '',
      usageData: null,
      loading: true,
      tick: null,
      showComparison: false,
      showTemperature: false,
      timeframeExceeded: false,
      goToDashboard: false,
      isCompany: false,
      companies: null,
      showInfo: false,
      frontPageLink: '',
      maximumResolution: '',
      unit: '',
      currentPeriodTemperatureFetched: false,
      currentPeriodElectricityFetched: false,
      consumptionDataFailed: false,
      chartLoading: true
    };

    this.getFrontpageLink = this.getFrontpageLink.bind(this);
  }

  parseQueryString = (qs: string): Params => {
    const withoutQuestionMark = qs.substring(1, qs.length);

    const paramsArray: string[] = withoutQuestionMark.split('&');
    return paramsArray
      .map((param: string) => param.split('='))
      .reduce(
        (acc: Params, cur) => {
          acc[cur[0]] = cur[1];
          return acc;
        },
        { start: '', end: '', level: '', frame: '' }
      );
  };

  updateRoute = (pathname: string, usageData?: UsageData) => {
    if (usageData) {
      const params = {
        start: usageData.start,
        end: usageData.end,
        frame: usageData.frame,
        level: usageData.level
      };
      this.props.navigate(pathname + buildQueryString(params));
    }
  };
  getChartDataByUsageData = async (usageData: UsageData) => {
    if (usageData) {
      let temperatureData = null;
      // If current period data is not already fetched - load the data
      // this is normally the initial fetching after component is rendered.
      // currentPeriodTemperatureFetched boolean is reseted in updateUsageData() if timeframe is updated
      if (
        !this.state.currentPeriodTemperatureFetched &&
        this.state.showTemperature &&
        usageData.level !== 'quarter-hour'
      ) {
        temperatureData = await this.loadTemperatureData(usageData.start, usageData.end, usageData);
        this.setState({ currentPeriodTemperatureFetched: true });
      }

      // Fetch temperature2 (previous year) only if showComparison and showTemperature = true
      const temperatureData2 =
        this.state.showComparison &&
        this.state.showTemperature &&
        usageData.level !== 'quarter-hour' &&
        (await this.loadTemperatureData(
          subtractOne(usageData.start, 'year'),
          subtractOne(usageData.end, 'year'),
          usageData
        ));

      let filledData: DataPoint[] | null | undefined = null;
      // Same logic as with the temperature data
      if (!this.state.currentPeriodElectricityFetched) {
        filledData = await this.loadData(usageData.start, usageData.end, false, usageData);

        // If no data is returned by the loadData fetcher,
        // we store the failure status until the next frame change
        if (!filledData) {
          this.setState({ consumptionDataFailed: true });
        }

        this.setState({ currentPeriodElectricityFetched: true });
      }
      // Same logic as with the temperature2 (previous year) data
      const filledData2 =
        this.state.showComparison &&
        (await this.loadData(
          subtractOne(usageData.start, 'year'),
          subtractOne(usageData.end, 'year'),
          true,
          usageData
        ));
      // Use already loaded temperatureData if available - otherwise fallback to state's tempData
      // This ensures we don't overwrite existing temperature data with an empty array and avoids re-fetching
      const tempBase = temperatureData || this.state.usageData?.tempData || [];
      const mergedTemp = mergeForComparison(tempBase, temperatureData2);
      // Update usageData with the merged temperature data (current + comparison)
      this.setState(() => {
        return {
          usageData: {
            ...usageData,
            tempData: mergedTemp
          }
        };
      });
      //Merge consumption data
      const mergedData = mergeForComparison(filledData || this.state.usageData?.data || [], filledData2);
      // Use prevState here so that we dont overwrite values we set in the previous setState()
      this.setState(
        prevState => ({
          barsHidden: true,
          loading: false,
          usageData: {
            ...prevState.usageData,
            ...usageData,
            data: mergedData,
            tempData: prevState.usageData?.tempData ?? []
          }
        }),
        this.handleData
      );
      this.setState({ chartLoading: false });
    }
  };

  canShowTimeSteps = () => {
    if (!this.state.usageData) return false;
    if (this.state.usageData.frame === 'year' || this.state.usageData.frame === 'month') {
      return false;
    }
    const date1 = moment(this.state.usageData.start);
    const date2 = moment(this.state.usageData.end);
    if (this.state.usageData.frame === 'custom' && date2.diff(date1, 'days') > 7) {
      return false;
    }
    return true;
  };

  handleShowTemperature = () => {
    this.setState({ showTemperature: !this.state.showTemperature });
  };

  handleShowInfo = () => {
    this.setState({ showInfo: !this.state.showInfo });
  };

  componentDidMount = async () => {
    window.scroll(0, 0);
    const { params, location } = this.props;
    const initialDate = params.initialDate || moment().format('YYYY');
    const companyPath = window.location.pathname.split('/')[1] === 'yritys';
    const role = this.getFrontpageLink(LoginStore.type);
    this.setState({ frontPageLink: role });

    if (!this.props.LoginStore.customerData || !this.props.LoginStore.customerData.length) {
      if (this.props.LoginStore.type == 'consumer') {
        await LoginStore.login('consumer', true);
      } else {
        await LoginStore.login('company', true);
      }
    }

    if (companyPath) {
      const companies = (await getCompanyUserDetails()).companies;
      this.setState({ isCompany: true, companies });
    }

    // Get type from props (manager) or from pathname (consumer)
    const type = this.props.type || window.location.pathname.split('/')[companyPath ? 2 : 1];
    const getInitialUsageData = async (): Promise<UsageData | null> => getUsageData(type, initialDate);
    const initialUsageData = await getInitialUsageData();

    if (initialUsageData) {
      await this.getChartDataByUsageData(initialUsageData);
      this.updateRoute(location.pathname, initialUsageData);
    }

    const handlePopState = async () => {
      const { usageData } = this.state;
      const { search, pathname } = window.location;
      if (pathname !== this.props.location.pathname) return;

      const params: Params = this.parseQueryString(search);

      if (!params.start || !params.end || !params.level || !params.frame) {
        this.setState({
          goToDashboard: true
        });
      } else {
        const { start, end, frame, level } = params;
        const newUsageData = {
          ...(usageData as UsageData),
          start,
          end,
          frame,
          level
        };
        if (usageData?.data) {
          await this.getChartDataByUsageData(newUsageData);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    this.backListener = () => window.removeEventListener('popstate', handlePopState);
  };
  componentWillUnmount() {
    this.backListener();
  }
  onRouteChange = (/*location, action*/) => {
    // console.log(location, action)
  };

  updateUsageData = (usageData: UsageData) => {
    // Store previous usage data into a variable to compare dates
    const previousUsage = this.state.usageData;

    const timestepChanged = previousUsage?.level !== usageData.level;

    // If the timeframe has been updated we wanto to clear the currentYear checks booleans
    const timeframeChanged = previousUsage?.start !== usageData.start || previousUsage?.end !== usageData.end;

    const shouldResetFlags = timeframeChanged || timestepChanged;
    // Clear the currentYear boolean values to refetch the data
    // The currentYear booleans are used to limit the data fetching in the getChartDataByUsageData method
    if (shouldResetFlags) {
      this.setState(
        {
          chartLoading: true,
          currentPeriodElectricityFetched: false,
          currentPeriodTemperatureFetched: false,
          consumptionDataFailed: false,
          usageData: {
            ...usageData, // updating here this is possibly causing lag?
            tempData: []
          }
        },
        () => {
          // After the currentYear variables are erased -> fetch the data
          this.getChartDataByUsageData(usageData);
          this.updateRoute(this.props.location.pathname, usageData);
        }
      );
      // If timeframe is the same - we dont need to clear the currentYear variables. This is to prevent
      // overfetching data fetching in the getChartDataByUsageData method
    } else {
      this.getChartDataByUsageData(usageData);
      this.updateRoute(this.props.location.pathname, usageData);
    }
  };

  loadData = async (
    start_s: string,
    end_s: string,
    isFetchingComparison: boolean,
    usageData?: UsageData
  ): Promise<DataPoint[] | null | undefined> => {
    if (!usageData) return null;

    // prevent fetch of comparison data if something has gone wrong
    // with consumption fetch for the current timeframe
    // otherwise the new fetch response and setState will overwrite our error messages
    // and load erroneous data
    if (isFetchingComparison && this.state.consumptionDataFailed) {
      return null;
    }

    const id: string = this.props.id || this.props.params.itemId || '';
    const companyID = this.props.params.companyId;
    let start = '';
    let end = '';

    // Set API call date format by usage period
    if (usageData.frame === 'year' && usageData.level === 'month') {
      start = moment(start_s).format('YYYY-MM');
      end = moment(end_s).format('YYYY-MM');
    } else if (usageData.level === 'quarter-hour') {
      start = encodeURIComponent(moment(start_s).format('YYYY-MM-DD HH:mm:ss'));
      end = encodeURIComponent(moment(end_s).format('YYYY-MM-DD HH:mm:ss'));
    } else {
      start = moment(start_s).format('YYYY-MM-DD');
      end = moment(end_s).format('YYYY-MM-DD');
    }

    const path = usageData.level;
    const req = new Request(
      `${apiUrl}/${usageData.type}/${path}?start=${start}&end=${end}&mp=${id}&companyid=${companyID}&logintype=${this.props.LoginStore.type}`,
      {
        method: 'GET',
        mode: 'cors'
      }
    );

    try {
      const response = await fetch(req);
      // add error handling for unavailabily of quarter-hour data
      if (path === 'quarter-hour' && response.status === 404) {
        this.setState({
          errorMessage: 'Valitettavasti tarkempaa tietoa ei ole saatavilla tälle ajanjaksolle.',
          timeframeExceeded: false,
          loading: false
        });
        window.scrollTo(0, 100);
        setTimeout(() => this.props.navigate(-1), 4000);
      } else if (response.status === 404 && !isFetchingComparison) {
        this.setState({
          errorMessage: 'Pyydettyä tietoja ei löytynyt',
          consumptionDataFailed: true,
          loading: false
        });
        window.scrollTo(0, 0);
        return null;
      } else if (response.status === 401) {
        // Logout if unauthorized
        this.setState({
          errorMessage: 'Ei oikeuksia tietojen lataamiseen, kirjaudutaan ulos...',
          timeframeExceeded: false,
          loading: false
        });
        await LoginStore.logout();
        window.location.assign(window.location.origin);
        return null;
      } else if (response.status === 400) {
        this.setState({
          errorMessage:
            'Kulutuslukemia voi hakea enintään yhden vuoden ajalta. Päivitä sivu päästäksesi takaisin kulutuslukemiin',
          timeframeExceeded: true,
          loading: false
        });
      } else if (response.status === 403) {
        // Logout if invalid api call
        this.setState({
          errorMessage: 'Virhe tietoja ladatessa, kirjaudutaan ulos...',
          timeframeExceeded: false,
          loading: false
        });
        await LoginStore.logout();
        return null;
      } else if (response.ok) {
        this.setState({
          errorMessage: '',
          timeframeExceeded: false,
          loading: false
        });

        const result = await response.json();
        const { data, maximumResolution, unit } = result;
        this.setState({ maximumResolution, unit });

        // Add dates without datapoints
        const filledData: DataPoint[] | null = fillDates(start_s, end_s, usageData.level, usageData.frame, data, false);
        if (!filledData) return null;

        return filledData;
      } else {
        this.setState({
          errorMessage: 'Virhe tietoja ladatessa',
          timeframeExceeded: false,
          loading: false
        });
      }
    } catch (error) {
      console.error('Error fetching usage data');
      sessionStorage.clear();
      this.setState({
        errorMessage: 'Virhe tietoja ladatessa',
        timeframeExceeded: false,
        loading: false
      });
    }
  };

  loadTemperatureData = async (start_s: string, end_s: string, usageData?: UsageData) => {
    if (!usageData) return;
    const path = usageData.level;
    if (!usageData) return null;

    const { params } = this.props;
    if (!usageData) return null;

    const id = this.props.id || params.itemId;

    let start = '';
    let end = '';

    // Set API call date format by usage period
    if (usageData.frame === 'year') {
      start = moment(start_s).format('YYYY-MM');
      end = moment(end_s).format('YYYY-MM');
    } else if (usageData.level === 'quarter-hour') {
      start = encodeURIComponent(moment(start_s).format('YYYY-MM-DD HH:mm:ss'));
      end = encodeURIComponent(moment(end_s).format('YYYY-MM-DD HH:mm:ss'));
    } else {
      start = moment(start_s).format('YYYY-MM-DD');
      end = moment(end_s).format('YYYY-MM-DD');
    }

    const req = new Request(`${apiUrl}/temperature/${path}?start=${start}&end=${end}&mp=${id}`, {
      method: 'GET',
      mode: 'cors'
    });

    const response = await fetch(req);
    const result = await response.json();
    const { data } = result;

    // Add dates without datapoints
    const filledData: DataPoint[] | null = fillDates(start_s, end_s, usageData.level, usageData.frame, data, true);
    if (!filledData) return null;
    return filledData;
  };

  toggleComparison = () => {
    const show = !this.state.showComparison;
    // Use setState callback to ensure showComparison is updated before updating usage data
    this.setState({ showComparison: show }, () => {
      if (this.state.showComparison && this.state.usageData) {
        this.updateUsageData(this.state.usageData);
      }
    });
  };

  toggleTemperature = () => {
    const showTemperature = !this.state.showTemperature;

    // Use setState callback to ensure showTemperature is updated before updating usage data
    this.setState({ showTemperature }, () => {
      if (this.state.showTemperature && this.state.usageData) {
        this.updateUsageData(this.state.usageData);
      }
    });
  };

  handleData = (): null => {
    const { usageData } = this.state;
    if (!usageData || !usageData.data) return null;

    const sumWholeNumber = usageData.data.map(datapoint => datapoint.v).reduce((total, amount) => total + amount, 0);
    /**
     * We use Math.round here to show decimals instead of toFixed()
     * to keep value as a number.
     * We dont want to switch the type to string since the animatedvalue
     * component doenst support string values.
     */
    const sum =
      usageData.type === 'electricity'
        ? Math.round(sumWholeNumber * 100) / 100
        : Math.round(sumWholeNumber * 1000) / 1000;

    const productionSum = () => {
      const totalProduction = usageData.data
        .map(datapoint => datapoint.vp as number)
        .reduce((total, amount) => total + amount, 0);

      const roundedProductionSum =
        usageData.type === 'electricity'
          ? Math.round(totalProduction * 100) / 100
          : Math.round(totalProduction * 1000) / 1000;

      return roundedProductionSum;
    };

    // Get and format timeframe shown on top of the graph
    const timeframe = this.getPeriod(usageData);

    // Format data timestamps on x-axis
    const tick = this.formatTimestamps(usageData);

    // Get graph className used for axis formatting
    // axis-1 shows all values, axis-2 every other value and so forth
    const axisClass = this.getGraphClassName(usageData);

    this.setState({
      axisClass: axisClass,
      sum: sum,
      productionSum: productionSum(),
      tick: tick,
      timeframe: timeframe,
      barsHidden: false
    });

    return null;
  };

  getPeriod = (usageData: UsageData): string => {
    // Get timestamps
    let firstTimestamp;
    let lastTimestamp;

    // Format timestamps
    if (usageData.frame === 'year') {
      firstTimestamp = moment
        .utc(usageData.start)
        .startOf('month')
        .format('DD.MM.YYYY');
      lastTimestamp = moment
        .utc(usageData.end)
        .endOf('month')
        .format('DD.MM.YYYY');
    } // add timestamp format for quarterhourly data
    else if (usageData.frame === 'hour') {
      firstTimestamp = moment.utc(usageData.start).format('DD.MM.YYYY HH:mm');
      lastTimestamp = moment.utc(usageData.end).format('HH:mm');
    } else {
      firstTimestamp = moment.utc(usageData.start).format('DD.MM.YYYY');
      lastTimestamp = moment.utc(usageData.end).format('DD.MM.YYYY');
    }

    let period;
    // Build timestamp string
    if (usageData.frame === 'custom') {
      // Use first and last value
      period = `${firstTimestamp} - ${lastTimestamp}`;
    } else if (usageData.frame === 'day' && usageData.level === 'hour') {
      // Show only last value
      period = lastTimestamp;
    } else {
      // Use first and last value
      period = `${firstTimestamp} - ${lastTimestamp}`;
    }

    return period;
  };

  formatTimestamps = (usageData: UsageData): Tick => {
    const level = usageData.level;
    const tick = {
      labelFormat: 'DD.MM',
      hoverFormat: 'DD.MM.',
      length: 0,
      hoverFormatExtended: 'DD.MM.YY'
    };
    if (level === 'hour') {
      tick.labelFormat = 'HH';
      tick.hoverFormat = 'DD.MM.YYYY HH:mm';
      tick.hoverFormatExtended = 'DD.MM.YYYY HH:mm';
    } else if (level === 'quarter-hour') {
      tick.labelFormat = 'HH:mm';
      tick.hoverFormat = 'DD.MM.YYYY HH:mm';
      tick.hoverFormatExtended = 'DD.MM.YYYY HH:mm';
    } else if (level === 'month') {
      tick.labelFormat = 'MMM';
      tick.hoverFormat = 'MMMM';
      tick.hoverFormatExtended = 'MMMM YYYY';
      tick.length = 3;
    } else {
      tick.labelFormat = 'DD.MM.';
      tick.hoverFormat = 'DD.MM.';
      tick.hoverFormatExtended = 'DD.MM.YY';
    }

    return tick;
  };

  getFrontpageLink = (role: string) => {
    switch (role) {
      case 'consumer':
        return '/dashboard';
      case 'remUser':
        return '/isannointi/dashboard';
      case 'remCompany':
        return '/paakayttaja/dashboard';
      default:
        return '';
    }
  };

  getGraphClassName = (usageData: UsageData): string => {
    // Determine graph className used to determine timestamp frequency
    if (usageData.frame === 'custom' && usageData.level === 'hour') {
      return 'axis-0';
    } else if (usageData.level === 'hour') {
      return 'axis-0';
    } else if (usageData.level === 'quarter-hour') {
      return 'axis-3';
    } else if (usageData.frame === 'year') {
      // Show all months
      return 'axis-0';
    } else {
      // Determine value based on number of datapoints
      // Under 12 datapoints = axis-0
      return `axis-${Math.ceil(usageData.data.length / 12)}`;
    }
  };

  render() {
    const {
      usageData,
      loading,
      errorMessage,
      timeframe,
      tick,
      barsHidden,
      axisClass,
      sum,
      timeframeExceeded,
      goToDashboard,
      maximumResolution,
      unit
    } = this.state;
    const { LoginStore, customer, contract, params } = this.props;

    if (goToDashboard) return <Navigate to={this.state.frontPageLink} />;

    const wrapperClass = `wrapper detailspage ${window.location.pathname.split('/')[this.state.isCompany ? 2 : 1]} ${
      this.state.isCompany ? 'consumption-view container' : ''
    }`;

    const id = this.props.id || params.itemId || '';
    const companyID = params.companyId;

    const barsClassnames = barsHidden
      ? 'barchart-wrapper hidden ' + axisClass
      : 'barchart-wrapper visible ' + axisClass;

    const contractData = LoginStore.customerData.find(customer =>
      customer.contracts.some(
        (contract: { meteringpointIdentifier: string }) => contract.meteringpointIdentifier === id
      )
    );
    const singleContractData = contractData?.contracts.find(
      (contract: { meteringpointIdentifier: string }) => contract.meteringpointIdentifier === id
    );
    const contractFromId = singleContractData;
    const companyName = contractData?.customerName;
    const meteringPoint = singleContractData?.meteringpointIdentifier;
    const meteringPointAddress = `${singleContractData?.address}, ${singleContractData?.zip} ${singleContractData?.city}`;
    const meteringPointGSRN = singleContractData?.gsrnIdentifier;

    if (loading) {
      return <Loading message="Ladataan käyttötietoja..." />;
    }
    if (!usageData || !usageData.data) {
      return <p className="errortext">Virhe käyttötietoja ladatessa</p>;
    }
    return (
      <div className={wrapperClass}>
        {errorMessage || timeframeExceeded ? (
          <p className="errortext">{errorMessage}</p>
        ) : (
          <>
            {this.state.isCompany ? (
              <div className="consumption-view-header">
                <button className="back-button" onClick={() => this.props.navigate('/yritys/yleisnakyma')}>
                  <Chevron />
                  Takaisin
                </button>
                <div className="title-wrapper">
                  <h1>
                    {meteringPointAddress} <br />
                    {!meteringPointGSRN ? (
                      <>{`Käyttöpaikka: ${meteringPoint}`}</>
                    ) : (
                      <>{`Käyttöpaikkatunnus GSRN: ${meteringPointGSRN}`}</>
                    )}
                    <br />
                    {usageData.type === 'electricity' ? 'Sähkönkulutus' : 'Kaukolämmön kulutus'}
                  </h1>
                  <p>{companyName}</p>
                </div>
              </div>
            ) : (
              <div className="container">
                <div className="btn-row">
                  <BackButton to={this.state.frontPageLink} />
                  <h1 className="details-header">{usageData.title}</h1>
                </div>
              </div>
            )}
            <div className={this.state.isCompany ? 'actions-and-data-wrapper' : ''}>
              <div className={this.state.isCompany ? 'actions-and-data' : ''}>
                <div className="time-selection">
                  {this.state.isCompany ? <p>Näytä</p> : null}
                  <TimeframeSelector
                    usageData={usageData}
                    updateUsageData={this.updateUsageData}
                    userType={LoginStore.type}
                    isCompany={this.state.isCompany}
                  />
                </div>

                {this.state.isCompany ? <TotalUsageCircle sum={sum} heading={'Kulutus'} unit={unit} /> : <></>}
              </div>
              <div className="timestep-placeholder">
                {this.canShowTimeSteps() && (
                  <TimeStepSelector
                    usageData={usageData}
                    updateUsageData={this.updateUsageData}
                    userType={LoginStore.type}
                    isCompany={this.state.isCompany}
                    maximumResolution={this.state.maximumResolution}
                  />
                )}
              </div>

              <div className={this.state.isCompany ? 'comparison-select-wrapper' : 'comparison-select-flex-wrapper'}>
                <div className="comparison-select">
                  <input
                    type="checkbox"
                    id="showComparison"
                    checked={!!this.state.showComparison}
                    onChange={this.toggleComparison}
                  />
                  <label htmlFor="showComparison">Vertaa vuoden takaiseen</label>
                </div>
                <div className="comparison-select">
                  <input
                    type="checkbox"
                    id="showTemperature"
                    checked={!!this.state.showTemperature}
                    onChange={this.toggleTemperature}
                    disabled={this.state.usageData?.level === 'quarter-hour'}
                  />
                  <label htmlFor="showTemperature">Näytä keskilämpötilatiedot</label>
                </div>

                {usageData.type === 'electricity' && (
                  <div
                    className={this.state.showInfo ? 'data-info-wrapper expand' : 'data-info-wrapper'}
                    onClick={this.handleShowInfo}
                  >
                    <i className={`fa fa-info-circle`} title="lisää tietoja" />
                    <div className="data-info">
                      Kulutusmittaukset 23.01.01 jälkeen esitetään nettosummana, eli tuotantotiedot on vähennetty
                      kokonaissummasta
                    </div>
                  </div>
                )}
              </div>

              {this.state.isCompany ? <></> : <AnimatedUsage usageSum={sum} type="heat" heading={'Kulutus'} />}
            </div>

            <React.Fragment>
              <NavigationArrows usageData={usageData} updateUsageData={this.updateUsageData} />
              <GraphTitle
                location={id}
                timeframe={timeframe}
                sum={sum}
                contractFromId={contractFromId ?? { address: '' }}
                isProductionData={false}
                unit={unit}
              />

              <div className={barsClassnames}>
                {/* barchart-wrapper hidden + axisClass */}
                <p className="graph-loading-text">{this.state.chartLoading && 'Ladataan tietoa'}</p>
                <BarChart
                  data={usageData.data}
                  tempData={usageData.tempData}
                  unit={unit}
                  usageData={usageData}
                  timeframe={timeframe}
                  id={id}
                  height="400"
                  tick={tick || null}
                  showComparison={this.state.showComparison}
                  showTemperature={this.state.showTemperature}
                  updateUsageData={this.updateUsageData}
                  contractFromId={contractFromId}
                  maximumResolution={maximumResolution}
                  scrollable={this.canShowTimeSteps()}
                  loading={this.state.chartLoading}
                />
              </div>

              {this.state.isCompany && usageData.type !== 'heat' ? (
                <TotalUsageCircle sum={this.state.productionSum as number} heading={'Tuotanto'} unit={unit} />
              ) : (
                usageData.type !== 'heat' && (
                  <>
                    <AnimatedUsage usageSum={this.state.productionSum as number} type="heat" heading={'Tuotanto'} />
                    <p className="graph-usage-unit">{unit}</p>
                  </>
                )
              )}

              <ExcelButton
                id={id}
                type={usageData.type}
                usageData={usageData}
                customer={customer}
                contract={contract}
                LoginStore={LoginStore}
                isCompany={this.state.isCompany}
                companyID={companyID}
              />
            </React.Fragment>
          </>
        )}
      </div>
    );
  }
}

const InjectedDetailsPage = inject('LoginStore')(observer(DetailsPage));

const DetailsPageWrapper = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const finalId = params.itemId ?? '';
  const finalType = params.type ?? '';

  return <InjectedDetailsPage id={finalId} type={finalType} navigate={navigate} location={location} params={params} />;
};

export default DetailsPageWrapper;
